import { useCallback, useImperativeHandle, useState, forwardRef } from 'react';
import { Container, Row } from 'reactstrap';
import { Modal, Button } from '@octano/global-ui';
import { ClassroomApiResponse } from '../../types';
import { PastReservationsContent } from './content/PastReservationsContent';
import { FutureReservationsContent } from './content/FutureReservationsContent';
import { ConfirmDeleteContent } from './content/ConfirmDeleteContent';
import useTexts from '../../hooks/useTexts';

interface ReservationDetailsModalProps {
  onConfirmDelete: (classroom: ClassroomApiResponse) => Promise<void>;
}

export interface ReservationDetailsModalMethods {
  open: (classroom: ClassroomApiResponse) => void;
  close: () => void;
}

const ReservationDetailsModal = forwardRef<
  ReservationDetailsModalMethods,
  ReservationDetailsModalProps
>(({ onConfirmDelete }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [classroom, setClassroom] = useState<ClassroomApiResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const texts = useTexts();

  const open = useCallback((classroomData: ClassroomApiResponse) => {
    setClassroom(classroomData);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    if (isDeleting) return;
    setIsOpen(false);
    setClassroom(null);
  }, [isDeleting]);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const handleConfirmDelete = useCallback(async () => {
    if (classroom && !isDeleting) {
      setIsDeleting(true);
      try {
        await onConfirmDelete(classroom);
        close();
      } catch (error) {
      } finally {
        setIsDeleting(false);
      }
    }
  }, [classroom, isDeleting, onConfirmDelete, close]);

  const renderContent = () => {
    if (!classroom) return null;

    // Check if there are current or future reservations
    if (
      classroom.currentOrFutureReservations.lessons.length > 0 ||
      classroom.currentOrFutureReservations.events.length > 0
    ) {
      return (
        <FutureReservationsContent
          lessons={classroom.currentOrFutureReservations.lessons}
          events={classroom.currentOrFutureReservations.events}
        />
      );
    }

    // Check if there are past reservations
    if (classroom.hasPastReservations) {
      return <PastReservationsContent />;
    }

    return <ConfirmDeleteContent />;
  };

  const canDelete =
    classroom &&
    !classroom.hasPastReservations &&
    classroom.currentOrFutureReservations.lessons.length === 0 &&
    classroom.currentOrFutureReservations.events.length === 0;

  const modalSize = canDelete ? 'md' : 'lg';

  return (
    <Modal isOpen={isOpen} size={modalSize} toggle={close}>
      <Container fluid>
        {renderContent()}

        <Row className="justify-content-center" style={{ marginTop: '50px' }}>
          <Button
            onClick={close}
            text={
              canDelete ? texts.delete.buttons.cancel : texts.delete.buttons.ok
            }
            outlined
            disabled={isDeleting}
            style={canDelete ? { width: '200px' } : { width: '400px' }}
          />

          {canDelete && (
            <Button
              onClick={handleConfirmDelete}
              text={texts.delete.buttons.delete}
              loading={isDeleting}
              disabled={isDeleting}
              style={{ width: '200px', marginLeft: '20px' }}
            />
          )}
        </Row>
      </Container>
    </Modal>
  );
});

export default ReservationDetailsModal;
